import type { NextApiRequest } from 'next'
import { IncomingMessage } from 'http'
import { NextRequest } from 'next/server'
import { HEADER_KEYS, HeaderName, RequestHeadersSchema } from '@reward-platform/shared-schemas'
import { getPlatformType } from '~/utils/platform'
import { NEXT_API_PATH } from '~/utils/apiConfig'
import { getBaseUrlFromServer } from './baseUrl'
import { RequestInvalidError } from '../errors/RequestInvalidError'
import { getPlatformOpCoFromRequest } from './platform'

/**
 * Generic type for the different types of next requests
 * @IncomingMessage standard http request
 * @NextApiRequest next api request that extends IncomingMessage
 * @NextRequest next middleware request that extends Request
 */

export type NextRequestParameter = IncomingMessage | NextApiRequest | NextRequest

// Helper to check if request is coming from middleware
const isEdgeRequest = (req: NextRequestParameter): req is NextRequest => {
  return req instanceof NextRequest
}

export const getHeadersFromRequest = (req: NextRequestParameter): Headers => {
  return isEdgeRequest(req)
    ? new Headers(req.headers) // If it's NextRequest, we can directly use req.headers
    : new Headers(Object.entries(req.headers || {}) as [string, string][]) // For IncomingMessage/NextApiRequest, convert headers object to Headers
}

// Safely extract headers depending on the type of request
export const getHeaderFromRequest = (req: NextRequestParameter, key: string) => {
  const headers = getHeadersFromRequest(req)
  return headers.get(key)
}

export const normaliseNextRequest = (req: NextRequestParameter): IncomingMessage => {
  if (isEdgeRequest(req)) {
    // It's a NextRequest (Edge Middleware)
    const headers: Record<string, string | undefined> = {}
    getHeadersFromRequest(req).forEach((value, key) => {
      headers[key] = value
    })

    return {
      url: req.url,
      method: req.method.toUpperCase() ?? 'GET',
      headers,
    } as IncomingMessage
  }

  return req
}

// NextApiRequest utils
export const parseRequestBody = <T>(req: NextApiRequest): T => {
  if (typeof req.body === 'string') {
    return JSON.parse(req.body)
  }

  return req.body
}

// IncomingMessage utils
export const validateRequestHeaders = (headers: Headers) => {
  const rawHeaders: Partial<Record<HeaderName, string>> = {}
  const allowedHeaderKeys = Object.values(HEADER_KEYS)

  allowedHeaderKeys.forEach((key) => {
    const headerValue = headers.get(key)
    if (headerValue) {
      rawHeaders[key] = headerValue
    }
  })

  const headersParse = RequestHeadersSchema.safeParse(rawHeaders)

  if (headersParse.error) {
    throw new RequestInvalidError('Invalid request headers')
  }

  return rawHeaders
}

export const createPlatformRequestHeaders = (req: IncomingMessage): Headers => {
  // Create a new Headers object
  const requestHeaders = getHeadersFromRequest(req)

  const opCo = getPlatformOpCoFromRequest(req)
  requestHeaders.set(HEADER_KEYS.partnerCode, opCo)

  const platformType = getPlatformType()
  requestHeaders.set(HEADER_KEYS.platformType, platformType)

  const apiBaseUrl = getBaseUrlFromServer(req) + NEXT_API_PATH
  requestHeaders.set(HEADER_KEYS.apiCallbackUrl, apiBaseUrl)

  return requestHeaders
}

// Used for validating headers passed to BFF endpoints
// Should be able to remove this once services are moved to FE server
export const getCommonRequestHeaders = (req: IncomingMessage) => {
  const reqHeaders = getHeadersFromRequest(req)
  return validateRequestHeaders(reqHeaders)
}
